
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Ticket } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { tr } from "@/i18n/translations";
import LanguageSwitch from "@/components/LanguageSwitch";

const NasaMyner = () => {
  const { lang } = useLanguage();
  const [videoOpen, setVideoOpen] = useState(false);

  const steps = [
    {
      num: "01",
      title: tr("myner", "step1Title", lang),
      body: tr("myner", "step1Body", lang),
      color: "var(--na-green)",
    },
    {
      num: "02",
      title: tr("myner", "step2Title", lang),
      body: tr("myner", "step2Body", lang),
      color: "var(--na-orange)",
    },
    {
      num: "03",
      title: tr("myner", "step3Title", lang),
      body: tr("myner", "step3Body", lang),
      color: "var(--na-green)",
    },
  ];

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="myner-page"
    >
      <LanguageSwitch />
      {videoOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: "rgba(0,0,0,0.92)" }}
          onClick={() => setVideoOpen(false)}
        >
          <div className="relative w-full max-w-3xl mx-4" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setVideoOpen(false)}
              className="mb-3 flex items-center gap-2 na-btn na-btn-ghost text-xs"
              style={{ letterSpacing: "0.2em" }}
            >
              ← NAZAD
            </button>
            <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: "6px", overflow: "hidden", border: "1px solid rgba(34,255,119,0.3)", boxShadow: "0 0 40px rgba(34,255,119,0.15)" }}>
              <iframe
                src="https://www.youtube.com/embed/ze46W5cvtsw?autoplay=1"
                title="NASA MYNER Adria Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
              />
            </div>
            <p className="mt-3 text-center text-xs text-zinc-500 uppercase tracking-[0.2em]">Klikni van videa ili pritisni NAZAD da zatvoris</p>
          </div>
        </div>
      )}
      <div className="na-grid" />
      <div className="na-noise" />

      <main className="relative z-10 mx-auto max-w-4xl px-5 py-12 sm:px-8 sm:py-16">
        {/* Back + logo */}
        <div className="na-anim na-delay-1 flex items-center justify-between">
          <Link to="/" className="na-btn na-btn-ghost" data-testid="back-button">
            <ArrowLeft size={14} />
            {tr("common", "back", lang)}
          </Link>
          <img
            src="/logo-nasa-myner.png"
            alt="NAŠA Myner Adria"
            data-testid="logo-myner"
           
            className="h-16 w-auto object-contain"
          />
        </div>

        {/* Heading */}
        <header className="na-anim na-delay-2 mt-10">
          <span className="na-tag">{tr("myner", "tag", lang)}</span>
          <h1
            className="na-display mt-5 text-3xl leading-tight sm:text-4xl lg:text-5xl"
            style={{ letterSpacing: "0.12em" }}
            data-testid="myner-title"
          >
            <span className="text-white">NAŠA </span>
            <span className="na-glow-orange na-pulse-orange">MYNER</span>
            <span className="text-white"> Adria</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-zinc-300 sm:text-lg">
            {tr("myner", "lead", lang)}
          </p>
        </header>

        {/* VIDEO UVOD baner */}
        <button
          onClick={() => setVideoOpen(true)}
          className="na-video-banner mt-8 w-full"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="na-play-icon">▶</div>
            <div className="text-left">
              <div className="na-display text-sm text-[var(--na-green)]" style={{ letterSpacing: "0.18em" }}>GIVEAWAY TUTORIJAL — KAKO SISTEM FUNKCIONIŠE</div>
              <div className="text-xs text-zinc-400 mt-1">NAŠA MYNER Adria · Giveaway inicijativa NAŠA Community ekosistema</div>
            </div>
          </div>
        </button>

        <hr className="na-divider" />

        <section className="na-anim na-delay-3 na-prose">
          <p>
            {tr("myner", "intro1pre", lang)}
          </p>
          <p>{tr("myner", "intro2", lang)}</p>
        </section>

        <div className="mt-10 space-y-6">
          <div className="na-section na-anim na-delay-3">
            <h3 className="na-display text-base text-[var(--na-green)]">
              {tr("myner", "s1Title", lang)}
            </h3>
            <div className="na-prose mt-3">
              <p>{tr("myner", "s1p", lang)}</p>
            </div>
          </div>

          <div className="na-section na-anim na-delay-4">
            <h3 className="na-display text-base text-[var(--na-orange)]">
              {tr("myner", "s2Title", lang)}
            </h3>
            <div className="na-prose mt-3">
              <p>
                {tr("myner", "s2pre", lang)}
                <strong>{tr("myner", "s2strong1", lang)}</strong>
                {tr("myner", "s2mid", lang)}
                <strong>{tr("myner", "s2strong2", lang)}</strong>
                {tr("myner", "s2rest", lang)}
              </p>
            </div>
          </div>

          <div className="na-section na-anim na-delay-4">
            <h3 className="na-display text-base text-[var(--na-green)]">
              {tr("myner", "s3Title", lang)}
            </h3>
            <div className="na-prose mt-3">
              <p>
                {tr("myner", "s3pre", lang)}
                <strong>{tr("myner", "s3strong1", lang)}</strong>
                {tr("myner", "s3mid", lang)}
                <strong>{tr("myner", "s3strong2", lang)}</strong>
                {tr("myner", "s3mid2", lang)}
                <strong>{tr("myner", "s3strong3", lang)}</strong>
                {tr("myner", "s3rest", lang)}
              </p>
            </div>
          </div>

          <div className="na-section na-anim na-delay-5">
            <h3 className="na-display text-base text-[var(--na-orange)]">
              {tr("myner", "s4Title", lang)}
            </h3>
            <div className="na-prose mt-3">
              <p>{tr("myner", "s4p", lang)}</p>
            </div>
          </div>
        </div>

        <hr className="na-divider" />

        {/* 3 koraka */}
        <section className="na-anim na-delay-4">
          <h2
            className="na-display text-xl text-white"
            style={{ letterSpacing: "0.18em" }}
          >
            {tr("myner", "stepsTitle", lang)}{" "}
            <span className="na-glow-green">
              {tr("myner", "stepsHighlight", lang)}
            </span>
          </h2>

          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
            {steps.map((step) => (
              <div
                key={step.num}
                className="na-section h-full"
                data-testid={`step-${step.num}`}
              >
                <div
                  className="na-display text-3xl"
                  style={{ color: step.color, letterSpacing: "0.1em" }}
                >
                  {step.num}
                </div>
                <h4
                  className="na-display mt-3 text-sm text-white"
                  style={{ letterSpacing: "0.18em" }}
                >
                  {step.title}
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="na-anim na-delay-5 mt-12 na-prose">
          <h3 className="na-display text-base text-[var(--na-orange)]">
            {tr("myner", "s5Title", lang)}
          </h3>
          <p>
            {tr("myner", "s5p1pre", lang)}
            <strong>{tr("myner", "s5p1strong", lang)}</strong>
            {tr("myner", "s5p1rest", lang)}
          </p>
          <p className="text-[var(--na-green)]">{tr("myner", "s5p2", lang)}</p>
        </section>

        <hr className="na-divider" />

        {/* Tutorijal video baner */}
        <button
          onClick={() => setVideoOpen(true)}
          className="na-video-banner w-full mb-6"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="na-play-icon">▶</div>
            <div className="text-left">
              <div className="na-display text-sm text-[var(--na-green)]" style={{ letterSpacing: "0.18em" }}>TUTORIJAL — PRIKAZ PLATFORME</div>
              <div className="text-xs text-zinc-400 mt-1">Pogledajte kako funkcioniše NAŠA MYNER Adria platforma iznutra</div>
            </div>
          </div>
        </button>

        {/* Discord zajednica */}
        <a
          href="https://discord.gg/Z6AXUFpNG9"
          className="na-video-banner block w-full mb-3"
          data-testid="myner-discord-button"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="na-play-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" /><path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1" /></svg></div>
            <div className="text-left">
              <div className="na-display text-sm uppercase text-[var(--na-green)]" style={{ letterSpacing: "0.18em" }}>{tr("myner", "discordBtn", lang)}</div>
              <div className="text-xs text-zinc-400 mt-1">{tr("myner", "discordSub", lang)}</div>
            </div>
          </div>
        </a>
        <div className="mb-6 text-center">
          <a
            href={lang === "en" ? "https://www.nasha-hub.com/discord-uputstvo-en.html" : "https://www.nasha-hub.com/discord-uputstvo.html"}
            className="text-xs text-zinc-400 underline underline-offset-4 transition-colors hover:text-[var(--na-green)]"
            data-testid="myner-discord-guide"
          >
            {tr("myner", "discordGuide", lang)}
          </a>
        </div>

        {/* CTA */}
        <div
          className="na-anim na-delay-5 flex justify-center"
          data-testid="myner-cta"
        >
          <a
            href="https://www.nashaadria.com/prijava"
            className="na-btn na-btn-primary text-center"
            data-testid="myner-join-button"
            style={{ padding: "0.9rem 1.4rem", flexDirection: "column", gap: "4px" }}
          >
            <span>Pridružite se NAŠA zajednici</span>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <strong style={{ textDecoration: "underline" }}>Kupite svoj prvi tiket</strong> <Ticket size={16} className="shrink-0" />
            </span>
          </a>
        </div>

        {/* Uslovi korišćenja dugme */}
        <div className="mt-8 flex justify-center">
          <a
            href="/uslovi-koriscenja.pdf"
            data-testid="terms-button"
            className="flex items-center gap-2 rounded-sm border border-[var(--na-green)]/40 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.25em] text-[var(--na-green)] transition-all hover:border-[var(--na-green)] hover:bg-[var(--na-green)] hover:text-black"
          >
            {tr("common", "terms", lang)}
          </a>
        </div>

        {/* Community temelji */}
        <div
          className="na-anim na-delay-5 mt-14 flex flex-col items-center"
          data-testid="myner-community"
        >
          <div className="mb-6 text-[10px] uppercase tracking-[0.35em] text-zinc-500">
            {tr("common", "poweredBy", lang)}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            <img
              src="/logo-nasa-com.png"
              alt="NAŠA.com"
              className="h-10 w-auto object-contain opacity-85 transition-opacity duration-300 hover:opacity-100"
            />
            <img
              src="/logo-nasa-community.png"
              alt="NAŠA Community"
              className="h-10 w-auto object-contain opacity-85 transition-opacity duration-300 hover:opacity-100"
            />
          </div>
        </div>

        <footer className="mt-16 border-t border-white/5 pt-6 text-center text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          © {new Date().getFullYear()} NAŠA MYNER Adria
        </footer>
      </main>
    </div>
  );
};

export default NasaMyner;
